import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, MapPin, Plus, Pencil, Trash2, Star, StarOff, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { notifyAddressChanged } from "../../lib/addressBus";
import { lineWithFlat } from "../../lib/customerUnit";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import AddressDialog from "../../components/AddressDialog";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { toast } from "sonner";

export default function ManageAddresses() {
  const { user } = useAuth();
  // Where to return to on "back" — set by whichever screen linked here
  // (CustomerHeader's address chip passes the page it was rendered on:
  // Home, Shop, or Product Detail). Falls back to Profile, matching the
  // Profile → Manage Addresses menu item, which doesn't set it.
  const location = useLocation();
  const backTo = location.state?.from || "/app/profile";
  const [addresses, setAddresses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async ({ announce = false } = {}) => {
    setLoadError(null);
    try {
      const { data } = await api.get("/api/customer/addresses");
      setAddresses(data.addresses || []);
      // Tell the header's "Delivery to" chip to re-read after a change made
      // here (add / edit / delete / set-default), so going back to Home
      // doesn't show the previous address. Off for the initial load — nothing
      // changed, and it would bounce a redundant fetch around every screen.
      if (announce) notifyAddressChanged();
    } catch (e) {
      setLoadError(friendlyMessage(e));
    }
  };
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return <LoginRequired title="Sign in to manage addresses"/>;
  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load your addresses" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setDialogOpen(true); };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.label}"?`)) return;
    try {
      await api.delete(`/api/customer/addresses/${a.id}`);
      toast.success("Deleted");
      load({ announce: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onSetDefault = async (a) => {
    try {
      await api.post(`/api/customer/addresses/${a.id}/default`);
      toast.success(`"${a.label}" set as default`);
      load({ announce: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="page-enter pb-12 min-h-screen flex flex-col" data-testid="manage-addresses-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-4 sm:px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to={backTo} className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5"/>
          </Link>
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="w-5 h-5 text-brand-sky shrink-0"/> Manage Addresses
          </h1>
          {/* Top-right Add button so users never scroll to add a new address */}
          <button onClick={openNew}
                  className="h-10 px-3 sm:px-4 rounded-2xl bg-brand-ink text-white font-extrabold text-xs sm:text-sm inline-flex items-center gap-1.5 shrink-0"
                  data-testid="add-address-btn">
            <Plus className="w-4 h-4"/> <span className="hidden xs:inline">Add</span><span className="hidden sm:inline"> new</span>
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-5 space-y-3 flex-1 flex flex-col">
        {addresses.length === 0 ? (
          <EmptyState
            icon={MapPin}
            title="No saved addresses yet"
            description="Add a delivery address to start ordering."
            action={
              <Button onClick={openNew} className="h-11 px-5 rounded-2xl bg-brand-ink text-white font-extrabold" data-testid="addresses-empty-add-btn">
                <Plus className="w-4 h-4 mr-1.5"/> Add address
              </Button>
            }
            fullHeight
            testId="addresses-empty"
          />
        ) : (
          addresses.map((a, idx) => {
            // Legacy data may have multiple is_default=true rows; show the
            // badge only on the FIRST such entry to keep the UI honest. New
            // 'Set default' actions go through the backend which demotes
            // siblings, so this is purely a data-normalization safeguard.
            const firstDefaultIdx = addresses.findIndex((x) => x.is_default);
            const showBadge = !!a.is_default && idx === firstDefaultIdx;
            return (
            <div key={a.id} className="bg-white rounded-2xl border border-neutral-200 p-4" data-testid={`address-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-extrabold text-sm">{a.label}</div>
                    {showBadge && (
                      <span className="text-[9px] uppercase tracking-wider font-bold bg-brand-leaf/15 text-brand-leaf px-2 py-0.5 rounded-full" data-testid="default-badge">Default</span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-700 break-words">
                    {lineWithFlat(a)}{a.line2 ? `, ${a.line2}` : ""}
                  </div>
                  <div className="text-sm text-neutral-500">
                    {a.city}{a.pincode ? ` · ${a.pincode}` : ""}
                  </div>
                  {a.landmark && <div className="text-xs text-neutral-500 mt-1">Near {a.landmark}</div>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {!showBadge && (
                  <button onClick={() => onSetDefault(a)}
                          className="px-3 h-9 rounded-2xl bg-brand-sky/10 text-brand-sky text-xs font-extrabold inline-flex items-center gap-1"
                          data-testid={`set-default-${a.id}`}>
                    <Star className="w-3.5 h-3.5"/> Set default
                  </button>
                )}
                <button onClick={() => openEdit(a)}
                        className="px-3 h-9 rounded-2xl bg-neutral-100 text-neutral-700 text-xs font-extrabold inline-flex items-center gap-1"
                        data-testid={`edit-address-${a.id}`}>
                  <Pencil className="w-3.5 h-3.5"/> Edit
                </button>
                <button onClick={() => onDelete(a)}
                        className="px-3 h-9 rounded-2xl bg-brand-clay/10 text-brand-clay text-xs font-extrabold inline-flex items-center gap-1 ml-auto"
                        data-testid={`delete-address-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/> Delete
                </button>
              </div>
            </div>
            );
          })
        )}
      </main>

      <AddressDialog open={dialogOpen} onOpenChange={setDialogOpen}
                     initial={editing} onSaved={() => load({ announce: true })}/>
    </div>
  );
}
